import _ from "lodash";
import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from "react-router";
import axios from 'axios';

import { getAnalytics, logEvent } from "firebase/analytics";

import { Box, Button, CircularProgress, Container, MobileStepper, Typography } from '@mui/material';
import { CheckboxComponent, DoneComponent, RadioComponent, RatingComponent, TextFieldComponent } from './components';
import { toRunnerSurvey } from '../utils/surveyFormats';

import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';

const API_URL = process.env.REACT_APP_API_URL;
const API_TOKEN = process.env.REACT_APP_API_TOKEN;

const Home = () => {

    const location = useLocation();
    const analytics = getAnalytics();

    const query = new URLSearchParams(location.search);
    const reward_value = query.get('reward');
    const i_customer = query.get('i_customer');

    // Security validation: Ensure reward and customer parameters are valid numeric values
    const isValidReward = reward_value && /^\d+$/.test(reward_value.trim());
    const isValidCustomer = i_customer && /^\d+$/.test(i_customer.trim());
    const hasValidParams = isValidReward && isValidCustomer;

    const { survey_id } = useParams();
    const [activeStep, setActiveStep] = useState(0);
    const [stepHistory, setStepHistory] = useState([]);
    const [disabledStep, setDisabledStep] = useState(true);
    const [steps, setSteps] = useState(0);
    const [survey, setSurvey] = useState([]);
    const [survey_answers, setSurveyAnswers] = useState([]);
    const [surveys, setSurveys] = useState([]);
    const [surveysLoading, setSurveysLoading] = useState(true);
    const [surveysError, setSurveysError] = useState(null);
    const [surveyNotFound, setSurveyNotFound] = useState(false);

    // Fetch surveys from API on component mount
    useEffect(() => {
        const fetchSurveys = async () => {
            try {
                setSurveysLoading(true);
                setSurveysError(null);
                
                const response = await axios.get(`${API_URL}/api/v1/surveys/all`, {
                    headers: {
                        'Authorization': `Bearer ${API_TOKEN}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (response.data.status_code === 1001) {
                    const rawData = response.data.data;
                    // API may return { data: [...], meta: {} } or the array directly
                    const list = Array.isArray(rawData) ? rawData : (rawData && rawData.data) || [];
                    setSurveys(list);
                } else {
                    throw new Error(response.data.data || response.data.message || 'Failed to fetch surveys');
                }
            } catch (error) {
                if (process.env.NODE_ENV !== 'production') {
                    console.error('Error fetching surveys:', error);
                }
                setSurveysError(error.response?.data?.data || error.response?.data?.message || error.message || 'Failed to fetch surveys');
                // Set empty array on error to prevent crashes
                setSurveys([]);
            } finally {
                setSurveysLoading(false);
            }
        };

        fetchSurveys();
    }, []);

    const sendSurveyEvent = (surveyId, customerId) => {
        logEvent(analytics, 'survey_view', { survey_id: surveyId, i_customer: customerId });
    };



    useEffect(() => {
        if (!survey_id || surveysLoading || surveys.length === 0) return;
        const id = parseInt(survey_id, 10);
        (async () => {
            try {
                const raw = _.find(surveys, { id });
                if (!raw) {
                    if (process.env.NODE_ENV !== 'production') {
                        console.error(`Survey with id ${id} not found`);
                    }
                    setSurveyNotFound(true);
                    setSurvey([]);
                    return;
                }
                const surveyPayload = raw.survey;
                const runnerSurvey = toRunnerSurvey(surveyPayload);
                if (runnerSurvey && runnerSurvey.length > 0) {
                    setSteps(runnerSurvey.length + 1);
                    setSurvey(runnerSurvey);
                    setSurveyNotFound(false);
                    setActiveStep(0);
                    setStepHistory([]);
                } else {
                    if (process.env.NODE_ENV !== 'production') {
                        console.error(`Survey with id ${id} has no survey or questions`);
                    }
                    setSurveyNotFound(true);
                    setSurvey([]);
                }
                sendSurveyEvent(id, i_customer != null && isValidCustomer ? parseInt(i_customer, 10) : undefined);
            } catch (err) {
                if (process.env.NODE_ENV !== 'production') {
                    console.error(err);
                }
                setSurveyNotFound(true);
                setSurvey([]);
            }
        })();
    }, [survey_id, surveys, surveysLoading, i_customer, isValidCustomer]);

    const currentQuestion = Array.isArray(survey) && survey[activeStep] ? survey[activeStep] : null;
    const currentQuestionNumber = currentQuestion ? currentQuestion.number : activeStep + 1;

    useEffect(() => {
        if (survey_answers.length !== 0 && currentQuestionNumber !== undefined && currentQuestionNumber !== null) {
            const answer = _.find(survey_answers, { 'question_number': currentQuestionNumber });
            setDisabledStep(!answer);
        } else {
            setDisabledStep(true);
        }
    }, [survey_answers, activeStep, currentQuestionNumber]);

    const handleNext = (nextQuestionNumber = null, nextQuestionIndex = undefined) => {
        if (nextQuestionIndex !== undefined && nextQuestionIndex !== null && nextQuestionIndex >= 0 &&
            Array.isArray(survey) && nextQuestionIndex < survey.length) {
            setStepHistory((h) => [...h, activeStep]);
            setActiveStep(nextQuestionIndex);
            return;
        }
        if (nextQuestionNumber !== null) {
            if (!survey || !Array.isArray(survey) || survey.length === 0) {
                setStepHistory((h) => [...h, activeStep]);
                setActiveStep((prevActiveStep) => prevActiveStep + 1);
                return;
            }
            const matchingIndices = survey
                .map((q, idx) => (q.number === nextQuestionNumber ? idx : -1))
                .filter((idx) => idx !== -1);
            if (matchingIndices.length > 1 && process.env.NODE_ENV !== 'production') {
                console.warn('handleNext: multiple questions with number', nextQuestionNumber, 'at indices', matchingIndices, '- using first match. Prefer nextQuestionIndex when available.');
            }
            const questionIndex = matchingIndices[0] ?? -1;
            if (questionIndex !== -1) {
                setStepHistory((h) => [...h, activeStep]);
                setActiveStep(questionIndex);
            } else {
                setStepHistory((h) => [...h, activeStep]);
                setActiveStep((prevActiveStep) => prevActiveStep + 1);
            }
        } else {
            setStepHistory((h) => [...h, activeStep]);
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
        }
    };

    const handleBack = () => {
        if (stepHistory.length > 0) {
            const nextStep = stepHistory[stepHistory.length - 1];
            setStepHistory((prev) => prev.slice(0, -1));
            setActiveStep(nextStep);
        } else {
            setActiveStep((s) => s - 1);
        }
    };

    const handleAnswer = async (answer) => {
        setSurveyAnswers((prev) => {
            const without = prev.filter((sa) => sa.question_number !== answer.question_number);
            return [...without, answer];
        });
    };

    return (
        <>
            <Container sx={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }} maxWidth={'xs'}>
                {!hasValidParams ? (
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                        <Typography variant="body1" sx={{ color: '#e41f26' }}>
                            Invalid survey parameters. Please check your link and try again.
                        </Typography>
                    </Box>
                ) : surveysLoading ? (
                    <Box>
                        <CircularProgress color='primary' />
                    </Box>
                ) : surveysError ? (
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                        <Typography variant="body1" sx={{ color: '#e41f26' }}>
                            Unable to load survey. Please try again later.
                        </Typography>
                        {process.env.NODE_ENV !== 'production' && (
                            <Typography variant="body2" sx={{ color: '#666', fontSize: '0.9rem' }}>
                                {surveysError}
                            </Typography>
                        )}
                    </Box>
                ) : surveyNotFound ? (
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                        <Typography variant="body1" sx={{ color: '#e41f26' }}>
                            Unable to load survey. Please try again later.
                        </Typography>
                    </Box>
                ) : survey.length === 0 && survey_id ? (
                    <Box>
                        <CircularProgress color='primary' />
                    </Box>
                ) : survey.length === 0 ? (
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                        <Typography variant="body1" sx={{ color: '#e41f26' }}>
                            Unable to load survey. Please try again later.
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ width: '100%', mb: 10 }}>
                        {currentQuestion && (() => {
                            const question = currentQuestion;
                            if (question.type === 'radio') {
                                return <RadioComponent key={question.number} question={question} handleNext={handleNext} handleAnswer={handleAnswer} survey_answers={survey_answers} survey={survey} />;
                            }
                            if (question.type === 'rating') {
                                return <RatingComponent key={question.number} question={question} handleNext={handleNext} handleAnswer={handleAnswer} survey_answers={survey_answers} survey={survey} />;
                            }
                            if (question.type === 'textfield') {
                                return <TextFieldComponent key={question.number} question={question} handleNext={handleNext} handleAnswer={handleAnswer} survey_answers={survey_answers} survey={survey} />;
                            }
                            if (question.type === 'checkbox') {
                                return <CheckboxComponent key={question.number} question={question} handleNext={handleNext} handleAnswer={handleAnswer} survey_answers={survey_answers} survey={survey} />;
                            }
                            if (question.type === 'done') {
                                return <DoneComponent key={question.number} reward_value={reward_value} i_customer={parseInt(i_customer, 10)} survey_answers={survey_answers} survey_id={parseInt(survey_id, 10)} />;
                            }
                            return null;
                        })()}
                    </Box>
                )}
                {survey.length > 0 && hasValidParams && (
                <MobileStepper
                    color='primary'
                    variant="progress"
                    steps={steps - 1}
                    position="bottom"
                    activeStep={activeStep}
                    sx={{ maxWidth: 400, flexGrow: 1 }}
                    nextButton={
                        <Button size="small" onClick={handleNext} disabled={activeStep === steps || disabledStep}>
                            <KeyboardArrowRight />
                        </Button>
                    }
                    backButton={
                        <Button size="small" onClick={handleBack} disabled={activeStep === 0}>
                            <KeyboardArrowLeft />
                        </Button>
                    }
                />
                )}
            </Container>
        </>
    );
};
export default Home;
